import type { TextNode } from '@repo/api-contracts';
import { type Raster, rasterFrom } from '@repo/warp-engine';
import type { CreateContext, MeasureText } from '../types';
import type { TextLayout } from './layout-text';
import {
  FAUX_BOLD_STROKE_RATIO,
  FAUX_ITALIC_SKEW,
  fontString,
  REFERENCE_FONT_PX,
  STRIKETHROUGH_OFFSET_EM,
  UNDERLINE_OFFSET_EM,
  UNDERLINE_THICKNESS_EM,
} from './text-style';

export interface RasterizeTextInput {
  readonly node: TextNode;
  readonly layout: TextLayout;
  readonly familyName: string;
  readonly color: string;
  readonly pxPerMm: number;
  readonly createContext: CreateContext;
  readonly measure: MeasureText;
}

/**
 * 텍스트 상자를 px 래스터로 만든다. 글자는 주입된 컨텍스트가 그리고(폰트는 보통 굵기·정체),
 * 굵게·기울임·밑줄·취소선은 T3·T4 수식으로 직접 합성한다. 자간은 글자별 수동 전진으로 적용한다.
 */
export const rasterizeText = ({
  node,
  layout,
  familyName,
  color,
  pxPerMm,
  createContext,
  measure,
}: RasterizeTextInput): Raster => {
  const width = Math.max(1, Math.ceil(node.width * pxPerMm));
  const height = Math.max(1, Math.ceil(layout.heightMm * pxPerMm));
  const ctx = createContext(width, height);
  const fontPx = node.fontSizeMm * pxPerMm;
  ctx.font = fontString(fontPx, familyName);
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.lineJoin = 'round';
  ctx.lineWidth = node.bold ? fontPx * FAUX_BOLD_STROKE_RATIO : 1;
  const spacingPx = node.letterSpacingEm * fontPx;

  for (const line of layout.lines) {
    const baselinePx = line.baselineMm * pxPerMm;
    if (node.italic) ctx.setTransform(1, 0, -FAUX_ITALIC_SKEW, 1, FAUX_ITALIC_SKEW * baselinePx, 0);
    else ctx.setTransform(1, 0, 0, 1, 0, 0);
    let x = line.xMm * pxPerMm;
    for (const char of line.text) {
      ctx.fillText(char, x, baselinePx);
      if (node.bold) ctx.strokeText(char, x, baselinePx);
      x += (measure(char, familyName) / REFERENCE_FONT_PX) * fontPx + spacingPx;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const lineWidthPx = line.widthMm * pxPerMm;
    const thickness = UNDERLINE_THICKNESS_EM * fontPx;
    if (node.underline) {
      ctx.fillRect(
        line.xMm * pxPerMm,
        baselinePx + UNDERLINE_OFFSET_EM * fontPx,
        lineWidthPx,
        thickness,
      );
    }
    if (node.strikethrough) {
      ctx.fillRect(
        line.xMm * pxPerMm,
        baselinePx - STRIKETHROUGH_OFFSET_EM * fontPx - thickness / 2,
        lineWidthPx,
        thickness,
      );
    }
  }
  const { data } = ctx.getImageData(0, 0, width, height);
  return rasterFrom(width, height, new Uint8ClampedArray(data));
};
